import React, { useState, useEffect } from 'react';
import { withStyles } from '@mui/styles';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DocumentMSStyle from './DocumentMSStyle';
import { assignDocument, getUsers, updateAssignment, getAssignments } from './dmsApi';

const AssignDocumentDialog = (props) => {
  const { classes, open, onClose, recordId, document, onSuccess, existingAssignments } = props;
  const docId = recordId || (document && document.id);
  const docName = document?.document_name || 'this document';
  const [formData, setFormData] = useState({
    assigned_to: '',
    notes: '',
    document_priority: '',
  });
  const [loading, setLoading] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [error, setError] = useState(null);
  const [existingAssignment, setExistingAssignment] = useState(null);

  useEffect(() => {
    if (open) {
      fetchEmployees();
      checkExistingAssignment();
    }
  }, [open, docId, existingAssignments]);

  const checkExistingAssignment = async () => {
    if (!docId) return;
    
    try {
      // Get all assignments for this document
      const allAssignments = existingAssignments || await getAssignments();
      const docAssignments = Array.isArray(allAssignments) 
        ? allAssignments.filter(a => a.record_id === docId || a.record_id === String(docId))
        : [];
      
      // If there's an existing assignment, set it
      if (docAssignments.length > 0) {
        // For now, we'll use the first assignment if multiple exist
        // You might want to handle multiple assignments differently
        setExistingAssignment(docAssignments[0]);
        // Pre-fill the form with existing assignment data
        const assignedTo = docAssignments[0].assigned_to;
        const assignedToId = typeof assignedTo === 'object' 
          ? (assignedTo._id || assignedTo.id || assignedTo.user_id)
          : assignedTo;
        setFormData({
          assigned_to: assignedToId || '',
          notes: docAssignments[0].notes || '',
          document_priority: docAssignments[0].document_priority || '',
        });
      } else {
        setExistingAssignment(null);
        setFormData({ assigned_to: '', notes: '', document_priority: '' });
      }
    } catch (err) {
      console.error('Error checking existing assignment:', err);
      setExistingAssignment(null);
    }
  };

  const fetchEmployees = async () => {
    try {
      setLoadingEmployees(true);
      const response = await getUsers('pwc');
      // Handle the API response structure: { count, results: [...] }
      if (response?.results && Array.isArray(response.results)) {
        setEmployees(response.results);
      } else if (Array.isArray(response)) {
        setEmployees(response);
      } else if (response?.users) {
        setEmployees(response.users);
      } else if (response?.data) {
        setEmployees(Array.isArray(response.data) ? response.data : []);
      } else {
        setEmployees([]);
      }
    } catch (err) {
      console.error('Failed to load employees:', err);
      setEmployees([]);
    } finally {
      setLoadingEmployees(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.assigned_to) {
      setError('Assigned to is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const userId = localStorage.getItem('userId') || sessionStorage.getItem('userId');
      
      // Check if this is a reassignment (existing assignment exists)
      if (existingAssignment) {
        // Reassigning - use PUT to update existing assignment
        await updateAssignment(existingAssignment.id, {
          assigned_to: formData.assigned_to,
          notes: formData.notes,
          document_priority: formData.document_priority,
          updated_by: userId,
        });
      } else {
        // No existing assignment - create new one (POST)
        await assignDocument(docId, {
          ...formData,
          user_id: userId,
        });
      }

      setFormData({ assigned_to: '', notes: '', document_priority: '' });
      setExistingAssignment(null);
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      setError(err.detail || err.error || 'Failed to assign document');
      console.error('Assign document error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ assigned_to: '', notes: '', document_priority: '' });
    setExistingAssignment(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        style: {
          borderRadius: '16px',
          fontFamily: 'Montserrat',
        },
      }}
    >
      <DialogTitle 
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '24px 24px 16px 24px',
          fontSize: '20px',
          fontWeight: 600,
          color: '#131C4C',
          fontFamily: 'Montserrat',
        }}
      >
        Assign Document
        <IconButton
          onClick={handleClose}
          size="small"
          style={{ color: '#6E759A' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent style={{ padding: '0 24px 24px 24px' }}>
        <Box style={{ marginBottom: '16px', fontSize: '14px', color: '#6E759A', fontFamily: 'Montserrat' }}>
          {existingAssignment ? `Reassign '${docName}' to an employee` : `Assign '${docName}' to an employee`}
        </Box>
        
        <Box className={classes.formGroup} style={{ marginBottom: '24px' }}>
          <Box className={classes.label} style={{ marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#131C4C', fontFamily: 'Montserrat' }}>
            Select Employee *
          </Box>
          {loadingEmployees ? (
            <Box style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <FormControl fullWidth>
              <Select
                value={formData.assigned_to}
                onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                displayEmpty
                style={{
                  fontFamily: 'Montserrat',
                  fontSize: '14px',
                  color: '#131C4C',
                }}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#E0E0E0',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#131C4C',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#131C4C',
                  },
                }}
              >
                <MenuItem value="" disabled style={{ fontFamily: 'Montserrat', color: '#6E759A' }}>
                  Choose an employee
                </MenuItem>
                {employees.map((employee) => (
                  <MenuItem 
                    key={employee._id || employee.id || employee.user_id} 
                    value={employee._id || employee.id || employee.user_id}
                    style={{ fontFamily: 'Montserrat' }}
                  >
                    {employee.name || employee.user_name || employee.email || `Employee ${employee._id || employee.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
        
        <Box className={classes.formGroup} style={{ marginBottom: '24px' }}>
          <Box className={classes.label} style={{ marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#131C4C', fontFamily: 'Montserrat' }}>
            Priority
          </Box>
          <FormControl fullWidth>
            <Select
              value={formData.document_priority}
              onChange={(e) => setFormData({ ...formData, document_priority: e.target.value })}
              displayEmpty
              style={{
                fontFamily: 'Montserrat',
                fontSize: '14px',
                color: '#131C4C',
              }}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#E0E0E0',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#131C4C',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#131C4C',
                },
              }}
            >
              <MenuItem value="" style={{ fontFamily: 'Montserrat', color: '#6E759A' }}>
                Select Priority
              </MenuItem>
              <MenuItem value="high" style={{ fontFamily: 'Montserrat' }}>
                High Priority
              </MenuItem>
              <MenuItem value="medium" style={{ fontFamily: 'Montserrat' }}>
                Medium Priority
              </MenuItem>
              <MenuItem value="low" style={{ fontFamily: 'Montserrat' }}>
                Low Priority
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
        
        <Box className={classes.formGroup}>
          <Box className={classes.label} style={{ marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#131C4C', fontFamily: 'Montserrat' }}>
            Notes (Optional)
          </Box>
          <TextField
            className={classes.input}
            fullWidth
            multiline
            rows={4}
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Add any instructions or notes...."
            style={{ fontFamily: 'Montserrat' }}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontFamily: 'Montserrat',
                fontSize: '14px',
              },
            }}
          />
        </Box>
        {error && (
          <Box 
            className={classes.error} 
            style={{ 
              marginTop: '16px', 
              padding: '12px', 
              backgroundColor: '#FFEBEE', 
              color: '#C62828', 
              borderRadius: '8px',
              fontSize: '14px',
              fontFamily: 'Montserrat',
            }}
          >
            {error}
          </Box>
        )}
      </DialogContent>
      <DialogActions style={{ padding: '16px 24px 24px 24px', gap: '12px' }}>
        <Button 
          onClick={handleClose}
          style={{
            textTransform: 'none',
            color: '#131C4C',
            fontFamily: 'Montserrat',
            fontSize: '14px',
            fontWeight: 500,
            padding: '8px 24px',
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          className={classes.button}
          disabled={loading || loadingEmployees}
          variant="contained"
          style={{
            textTransform: 'none',
            backgroundColor: '#131C4C',
            color: '#FFFFFF',
            fontFamily: 'Montserrat',
            fontSize: '14px',
            fontWeight: 500,
            padding: '8px 24px',
            '&:hover': {
              backgroundColor: '#0F1642',
            },
          }}
        >
          {loading ? <CircularProgress size={20} style={{ color: '#FFFFFF' }} /> : (existingAssignment ? 'Update Assignment' : 'Assign Document')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default withStyles(DocumentMSStyle)(AssignDocumentDialog);
